/**
 * EditClientModal Component
 * Modal interface for editing existing RevFinOps client information
 * Wraps ClientForm in a modal overlay with proper accessibility
 */

import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { ClientForm, ClientFormData } from './ClientForm'
import { updateClient, RevFinOpsClient } from '@/lib/revfinops-api'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface EditClientModalProps {
  client: RevFinOpsClient
  isOpen: boolean
  onClose: () => void
  onSuccess: (updatedClient: RevFinOpsClient) => void
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * EditClientModal - Modal dialog for editing client information
 *
 * Features:
 * - Modal overlay with backdrop blur
 * - Client form in edit mode
 * - API integration with updateClient
 * - Success/error handling
 * - Keyboard accessibility (ESC to close)
 * - Focus trap within modal
 * - Outside click to close
 * - Responsive design
 *
 * @param client - Client object to edit
 * @param isOpen - Modal visibility state
 * @param onClose - Callback to close modal
 * @param onSuccess - Callback when client is successfully updated
 */
export function EditClientModal({
  client,
  isOpen,
  onClose,
  onSuccess
}: EditClientModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle form submission - calls updateClient API
   */
  const handleSubmit = async (data: ClientFormData) => {
    try {
      setLoading(true)
      setError(null)

      const response = await updateClient(client.id, data)

      if (response.success && response.data) {
        onSuccess(response.data)
        onClose()
      } else {
        setError(response.error?.message || 'Failed to update client')
      }
    } catch (err) {
      setError('An error occurred while updating the client')
      console.error('EditClientModal: Error updating client:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle cancel - closes modal without saving
   */
  const handleCancel = () => {
    setError(null)
    onClose()
  }

  /**
   * Handle overlay click - closes modal when clicking outside
   */
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel()
    }
  }

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Handle ESC key press to close modal
   */
  useEffect(() => {
    if (!isOpen) return

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [isOpen])

  /**
   * Prevent body scroll when modal is open
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null

  return (
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-client-title"
    >
      <div className="modal-content max-w-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-6">
          <h2
            id="edit-client-title"
            className="font-display text-2xl font-bold text-white"
          >
            Edit Client
          </h2>
          <button
            onClick={handleCancel}
            className="btn-ghost p-2"
            aria-label="Close modal"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="card border-l-4 border-error bg-error/10 mb-4">
            <p className="text-error font-body">{error}</p>
          </div>
        )}

        {/* Client Form */}
        <ClientForm
          mode="edit"
          initialData={client}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </div>
    </div>
  )
}

export default EditClientModal
